import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface PaginationProps {
  itemsPerPage: number;
  totalItems: number;
  paginate: (pageNumber: number) => void;
  currentPage: number;
}

export default function ListingsPagination({
  itemsPerPage,
  totalItems,
  paginate,
  currentPage,
}: PaginationProps) {
  const pageNumbers = [];

  for (let i = 1; i <= Math.ceil(totalItems / itemsPerPage); i++) {
    pageNumbers.push(i);
  }

  if (pageNumbers.length <= 1) return null;

  // Determine which page numbers to show
  let pagesToShow = [];

  if (pageNumbers.length <= 5) {
    pagesToShow = pageNumbers;
  } else {
    if (currentPage <= 3) {
      pagesToShow = [1, 2, 3, 4, "...", pageNumbers.length];
    } else if (currentPage >= pageNumbers.length - 2) {
      pagesToShow = [
        1,
        "...",
        pageNumbers.length - 3,
        pageNumbers.length - 2,
        pageNumbers.length - 1,
        pageNumbers.length,
      ];
    } else {
      pagesToShow = [
        1,
        "...",
        currentPage - 1,
        currentPage,
        currentPage + 1,
        "...",
        pageNumbers.length,
      ];
    }
  }

  return (
    <nav className="flex justify-center">
      <ul className="flex items-center space-x-1">
        <li>
          <Button
            variant="outline"
            size="icon"
            onClick={() => paginate(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="sr-only">Previous page</span>
          </Button>
        </li>

        {pagesToShow.map((page, index) => (
          <li key={index}>
            {page === "..." ? (
              <span className="px-3 py-2">...</span>
            ) : (
              <Button
                variant={currentPage === page ? "default" : "outline"}
                size="icon"
                onClick={() => paginate(Number(page))}
              >
                {page}
              </Button>
            )}
          </li>
        ))}

        <li>
          <Button
            variant="outline"
            size="icon"
            onClick={() =>
              paginate(Math.min(pageNumbers.length, currentPage + 1))
            }
            disabled={currentPage === pageNumbers.length}
          >
            <ChevronRight className="h-4 w-4" />
            <span className="sr-only">Next page</span>
          </Button>
        </li>
      </ul>
    </nav>
  );
}
